
import { useState, useEffect } from "react";
import { toast } from "sonner";

export interface GoogleMapsState {
  isLoaded: boolean;
  error: string | null;
  isInitializing: boolean;
}

export function useGoogleMaps() {
  const [state, setState] = useState<GoogleMapsState>({
    isLoaded: false,
    error: null,
    isInitializing: false
  });

  useEffect(() => {
    // Check if Google Maps is already loaded
    if (window.google?.maps) {
      console.log("Google Maps already loaded");
      setState({ isLoaded: true, error: null, isInitializing: false });
      return;
    }

    const initGoogleMaps = async () => {
      setState(prev => ({ ...prev, isInitializing: true }));
      
      // Validate API key - ensuring we're using the environment variable
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      console.log("API KEY CHECK:", apiKey ? "Key exists" : "Key missing");
      
      if (!apiKey) {
        const errorMessage = "Google Maps API key is missing. Please add VITE_GOOGLE_MAPS_API_KEY to your .env file.";
        console.error(errorMessage);
        setState({ isLoaded: false, error: errorMessage, isInitializing: false });
        toast.error("Google Maps API key is required", {
          description: "Please add VITE_GOOGLE_MAPS_API_KEY to your .env file"
        });
        return;
      }
      
      if (apiKey === "your_google_maps_api_key_here") {
        const errorMessage = "Google Maps API key is not set correctly. Please replace the placeholder with a valid API key.";
        console.error(errorMessage);
        setState({ isLoaded: false, error: errorMessage, isInitializing: false });
        toast.error("Google Maps API key is not configured", {
          description: "Please replace the placeholder with a valid API key"
        });
        return;
      }

      console.log("Loading Google Maps with key:", apiKey.substring(0, 5) + "...");
      
      try {
        await new Promise<void>((resolve, reject) => {
          const googleMapsScript = document.createElement("script");
          googleMapsScript.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
          googleMapsScript.async = true;
          googleMapsScript.defer = true;
          
          googleMapsScript.addEventListener("load", () => {
            console.log("Google Maps script loaded successfully");
            resolve();
          });

          googleMapsScript.addEventListener("error", (error) => {
            console.error("Failed to load Google Maps script:", error);
            reject(new Error("Failed to load Google Maps. Check your API key and network connection."));
          });

          document.head.appendChild(googleMapsScript);
        });
        
        setState({ isLoaded: true, error: null, isInitializing: false });
        toast.success("Google Maps loaded successfully");
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error loading Google Maps";
        console.error(errorMessage);
        setState({ isLoaded: false, error: errorMessage, isInitializing: false });
        toast.error("Google Maps Load Error", {
          description: "Unable to load Google Maps. Please check your API key."
        });
      }
    };

    initGoogleMaps();

    return () => {
      const script = document.querySelector(`script[src^="https://maps.googleapis.com/maps/api/js"]`);
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return state;
}
